import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, Upload } from 'lucide-react'
import { Badge, Field, PageHeader, StringListInput, notify } from '@/kit/ui'
import { EMPTY_FACTS } from '@/fixtures/data'
import { LOGO_MAX_BYTES, inApp } from '@/lib/inApp'
import { CONNECTION_LABEL, LOAN_TYPE_LABEL, PARTNER_TYPE_LABEL, canActivate, lenderLine, money, requiredChecks, slugify } from '@/lib/lender'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { refreshLender, useHubStore, whenSynced } from '@/store/hubStore'
import type { KeyFacts, Lender, LoanType, PartnerType } from '@/types'
import { LenderLogo } from './LenderBits'

function blank(): Lender {
  const at = new Date().toISOString()
  return {
    id: `l_${Date.now().toString(36)}`, slug: '', name: '', legalName: '', rbiCor: '', website: '',
    partnerType: 'nbfc', connection: 'link', lenders: [], hasLogo: false, logoPermission: null, appName: '', creditCheckNote: '', linkParams: {},
    grievance: { name: '', email: '', phone: '', designation: '', hours: '', address: '' }, brand: { color: '#0F7D6E' },
    shortLine: '', links: { web: '', play: '', appstore: '', privacy: '', grievancePolicy: '', fairPractices: '' },
    env: { uatBaseUrl: '', prodBaseUrl: '', keysSet: false, keyNames: [] }, envVars: [], ipWhitelist: { state: 'not_needed', date: null },
    usedBy: [], status: 'draft', activeSince: null, offReason: null,
    products: [{ slug: '', name: 'Personal loan', loanType: 'personal', active: true, keyFacts: { ...EMPTY_FACTS }, kfsFile: null, endpointId: null, endpointState: 'none' }],
    liveBatch: null, draftBatch: null, workflows: [], createdAt: at, updatedAt: at,
  }
}

const originOf = (url: string) => { try { return new URL(url.trim()).origin } catch { return '' } }

/**
 * Add a lender (PF-1, owner 2026-10-19: "get simple information name logo key facts feature and loan information and
 * utm link play store link and app store link"): one short screen. Name, logo and the UTM link are needed to go live
 * (link only); the rest is optional, and the API and legal details come later on the lender's page.
 */
export function AddLenderScreen() {
  const { user, can } = useCurrentUser()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { slug?: string } }
  const lenders = useHubStore((s) => s.lenders)
  const addLender = useHubStore((s) => s.addLender)
  const activate = useHubStore((s) => s.activateLender)
  const resume = location.state?.slug ? lenders.find((l) => l.slug === location.state?.slug && l.status === 'draft') : undefined
  const [l, setL] = useState<Lender>(() => resume ?? blank())
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const checks = useMemo(() => requiredChecks(l), [l])
  const slug = l.slug || slugify(l.name)
  const product = l.products[0]!
  const f = product.keyFacts
  const taken = !l.slug && !!slug && lenders.some((x) => x.slug === slug)

  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview) }, [logoPreview])

  const setField = (patch: Partial<Lender>) => setL((x) => ({ ...x, ...patch }))
  const setFacts = (patch: Partial<KeyFacts>) => setL((x) => ({ ...x, products: [{ ...x.products[0]!, keyFacts: { ...x.products[0]!.keyFacts, ...patch } }, ...x.products.slice(1)] }))
  const setLinks = (patch: Partial<Lender['links']>) => setL((x) => ({ ...x, links: { ...x.links, ...patch } }))

  /** The record as it is saved: the slug fixed, the website from the UTM link. A product that already has a slug keeps
   *  it (workflows and service endpoints point at it); only a new one gets lender.<name>. */
  const withSlug = (): Lender => ({
    ...l, slug,
    website: l.website || originOf(l.links.web),
    products: [{ ...product, slug: product.slug || `${slug}.${slugify(product.name) || 'personal'}`, keyFacts: { ...f, rateMaxPa: Math.max(f.rateMaxPa, f.rateMinPa) } }, ...l.products.slice(1)],
  })

  const saveDraft = (quiet = false) => {
    if (!slug) { notify.warning('Type the lender name first'); return null }
    if (taken) { notify.warning(`${slug} is taken`, 'Another lender already uses this name; choose another.'); return null }
    const saved = withSlug()
    setL(saved)
    addLender(saved, user.email)
    if (!quiet) notify.success('Draft saved', `${saved.name} is in the Lenders list as Draft.`)
    return saved
  }

  // the logo: on the Hub API it is uploaded at once (the draft is saved first); on the fixtures it is only shown here
  const uploadLogo = async (file: File) => {
    if (file.size > LOGO_MAX_BYTES) return notify.warning('That logo is too big', `${Math.round(LOGO_MAX_BYTES / 1024)} KB at most.`)
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return notify.warning('PNG, JPEG or WebP only')
    if (logoPreview) URL.revokeObjectURL(logoPreview)
    setLogoPreview(typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null)
    if (!inApp.available()) { setField({ hasLogo: true }); return }
    const saved = saveDraft(true)
    if (!saved) return
    setUploading(true)
    try {
      await whenSynced()
      await inApp.uploadLogo(saved.slug, file)
      await refreshLender(saved.slug)
      setField({ hasLogo: true })
      notify.success('Logo uploaded')
    } catch (err) {
      notify.error('Logo not uploaded', err instanceof Error ? err.message : String(err))
    } finally { setUploading(false) }
  }

  const goLive = async () => {
    const saved = saveDraft(true)
    if (!saved || !canActivate(saved)) return
    activate(saved.slug, user.email)
    await whenSynced()
    notify.success(`${saved.name} is live`, 'As a link: the card opens its UTM link. API details can be added later on its page.')
    navigate(`/lenders/${saved.slug}`)
  }

  const text = (label: string, value: string, onChange: (v: string) => void, props: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {}, required = false, help?: string) => (
    <Field label={label} required={required} help={help}>{(id) => <input id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} {...props} />}</Field>
  )
  const num = (label: string, k: keyof KeyFacts, props: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {}) => (
    <Field label={label}>{(id) => <input id={id} type="number" min={0} className="input" value={(f[k] as number) || ''} onChange={(e) => setFacts({ [k]: Number(e.target.value) || 0 })} {...props} />}</Field>
  )
  const missing = checks.filter((c) => !c.ok)

  return (
    <>
      <PageHeader title="Add a lender" subtitle="One short form. Name, logo and the UTM link are enough to go live; the rest is optional. API details come later, on the lender's page." above={<Link to="/lenders">Lenders</Link>} actions={<Badge tone="gray">Draft{l.name ? ` · ${l.name}` : ''}</Badge>} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_300px]">
        <div className="space-y-3">
          <section className="card">
            <h2 className="section-title">The lender</h2>
            <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
              {text('Lender name', l.name, (v) => setField({ name: v }), { placeholder: 'MoneyView' }, true, l.slug ? `Address name: ${l.slug} (fixed)` : slug ? `Address name: ${slug}${taken ? ' (taken: choose another name)' : ''}` : undefined)}
              <Field label="Type" help="A label only">{(id) => (
                <select id={id} className="input" value={l.partnerType} onChange={(e) => setField({ partnerType: e.target.value as PartnerType })}>
                  {(Object.keys(PARTNER_TYPE_LABEL) as PartnerType[]).map((k) => <option key={k} value={k}>{PARTNER_TYPE_LABEL[k]}</option>)}
                </select>
              )}</Field>
            </div>
            <Field label="Logo (PNG, JPEG or WebP)" required help={`${Math.round(LOGO_MAX_BYTES / 1024)} KB at most`}>{(id) => (
              <div className="flex flex-wrap items-center gap-3">
                {logoPreview ? <img src={logoPreview} alt="The new logo" className="h-11 w-11 rounded-md border border-slate-200 bg-white object-contain p-0.5" /> : <LenderLogo lender={{ ...l, slug }} />}
                <label htmlFor={id} className={`btn btn-secondary ${!l.name.trim() || uploading ? 'pointer-events-none opacity-60' : ''}`}>{uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {l.hasLogo ? 'Replace logo' : 'Choose logo'}</label>
                <input id={id} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" disabled={!l.name.trim() || uploading} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void uploadLogo(file) }} />
                <span className="text-2xs text-muted">{l.hasLogo ? 'Uploaded ✓' : !l.name.trim() ? 'Type the name first' : 'Not uploaded yet'}</span>
              </div>
            )}</Field>
          </section>

          <section className="card">
            <h2 className="section-title">Loan information <span className="font-normal text-muted">(optional)</span></h2>
            <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
              {text('Product', product.name, (v) => setL((x) => ({ ...x, products: [{ ...x.products[0]!, name: v }, ...x.products.slice(1)] })), { placeholder: 'Personal loan' })}
              <Field label="Loan type" help="The website's filters and Apply now">{(id) => (
                <select id={id} className="input" value={product.loanType} onChange={(e) => setL((x) => ({ ...x, products: [{ ...x.products[0]!, loanType: e.target.value as LoanType }, ...x.products.slice(1)] }))}>
                  {(Object.keys(LOAN_TYPE_LABEL) as LoanType[]).map((k) => <option key={k} value={k}>{LOAN_TYPE_LABEL[k]}</option>)}
                </select>
              )}</Field>
              {num('Amount up to (₹)', 'amountMax', { placeholder: '500000' })}
              {num('Interest rate from (% a year)', 'rateMinPa', { step: '0.01', placeholder: '14' })}
              {num('Tenure up to (months)', 'tenureMaxMonths', { placeholder: '36' })}
              {text('Processing fee', f.processingFee, (v) => setFacts({ processingFee: v }), { placeholder: 'Up to 2% + GST' })}
            </div>
            <Field label="Features" help="Press Enter after each, e.g. No collateral · Money in 24 hours">{(id) => <StringListInput id={id} value={f.extra} onChange={(extra) => setFacts({ extra })} placeholder="No collateral" />}</Field>
          </section>

          <section className="card">
            <h2 className="section-title">Links</h2>
            <Field label="How we work with them" help="Manual = no link or API: our team enters each lead on their partner portal (e.g. WeRize)">{(id) => (
              <select id={id} className="input" value={l.connection} onChange={(e) => setL((x) => ({ ...x, connection: e.target.value as Lender['connection'] }))}>
                {(Object.keys(CONNECTION_LABEL) as Lender['connection'][]).map((k) => <option key={k} value={k}>{CONNECTION_LABEL[k]}</option>)}
              </select>
            )}</Field>
            {text('UTM link', l.links.web, (v) => setLinks({ web: v }), { placeholder: 'https://lender.example/apply?utm_source=firstfund&utm_medium=referral' }, l.connection !== 'manual', l.connection === 'manual' ? 'Not needed for a manual partner' : 'The card opens this link')}
            <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
              {text('Play Store link (if they have an app)', l.links.play, (v) => setLinks({ play: v }), { placeholder: 'https://play.google.com/store/apps/details?id=…' })}
              {text('App Store link (if they have an app)', l.links.appstore, (v) => setLinks({ appstore: v }), { placeholder: 'https://apps.apple.com/in/app/…' })}
            </div>
          </section>
        </div>

        <aside className="space-y-3">
          <section className="card" aria-label="The card, as the customer sees it">
            <p className="eyebrow mb-2">The card</p>
            <div className="flex items-center gap-2">
              {logoPreview ? <img src={logoPreview} alt="" className="h-9 w-9 rounded-md border border-slate-200 bg-white object-contain p-0.5" /> : <LenderLogo lender={{ ...l, slug }} size={36} />}
              <div className="min-w-0"><b className="block truncate text-sm">{l.name || 'Lender name'}</b><span className="block text-2xs text-muted">{lenderLine(l)}</span></div>
            </div>
            {(f.amountMax > 0 || f.rateMinPa > 0 || f.tenureMaxMonths > 0 || f.processingFee) && (
              <dl className="mt-2 grid grid-cols-2 gap-1 text-2xs">
                {f.amountMax > 0 && <><dt className="text-muted">Amount up to</dt><dd>{money(f.amountMax)}</dd></>}
                {f.rateMinPa > 0 && <><dt className="text-muted">Interest from</dt><dd>{f.rateMinPa}% a year</dd></>}
                {f.tenureMaxMonths > 0 && <><dt className="text-muted">Tenure up to</dt><dd>{f.tenureMaxMonths} months</dd></>}
                {f.processingFee && <><dt className="text-muted">Processing fee</dt><dd>{f.processingFee}</dd></>}
              </dl>
            )}
            {f.extra.length > 0 && <ul className="mt-2 list-inside list-disc text-2xs">{f.extra.map((x) => <li key={x}>{x}</li>)}</ul>}
            <span className="btn btn-primary btn-sm mt-3 w-full justify-center">Visit {l.name || 'lender'}</span>
            {(l.links.play || l.links.appstore) && <p className="mt-1 text-center text-2xs text-muted">{[l.links.play && 'Play Store', l.links.appstore && 'App Store'].filter(Boolean).join(' · ')}</p>}
          </section>

          <section className="card">
            <p className="eyebrow mb-2">To go live</p>
            <ul className="space-y-1 text-xs" data-testid="add-lender-checks">
              {checks.map((c) => (
                <li key={c.key} className="flex items-start gap-2"><span className={`mono font-bold ${c.ok ? 'text-emerald-600' : 'text-red-600'}`}>{c.ok ? '✓' : '✗'}</span><span>{c.label}</span></li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => saveDraft()}>Save draft</button>
              <button type="button" className="btn btn-primary" disabled={!canActivate(l) || !can('lenders.activate')} onClick={() => void goLive()} title={!can('lenders.activate') ? 'An admin makes a lender live' : canActivate(l) ? undefined : 'Name, logo and UTM link first'}>Go live</button>
            </div>
            <p className="mt-2 text-2xs text-muted">{!can('lenders.activate') ? 'An admin makes it live; save the draft for them.' : missing.length ? `${missing.length} missing.` : 'Live as a link. Logged with your name.'}</p>
          </section>
        </aside>
      </div>
    </>
  )
}
